import { type Dispatch, type SetStateAction, useCallback, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useRouter } from '@/lib/router-events/patch-router/router';
import { prepareUrl } from '@/utils/urlHelper';
import Icon from '@/components/shared/Icons';
import TextLink from '@/components/shared/TextLink';
import dynamic from 'next/dynamic';
import { convertToSnakeCase } from '@/utils/analyticsClientHelper';

const Popper = dynamic(() => import('@/components/shared/popper/Popper'), { ssr: false });

const SORT_QUERY_PARAM = 'sort';

interface Props {
  sortOptions: { label: string; value: string }[];
  sortingLabel: string;
  setSortingType: Dispatch<SetStateAction<string>>;
  setSortingLabel: Dispatch<SetStateAction<string>>;
  sortByLabel: string;
  isSearchExpanded: boolean;
  setIsSearchExpanded: (b: boolean) => void;
}

export default function SortBy({
  sortOptions,
  sortingLabel,
  setSortingType,
  setSortingLabel,
  sortByLabel,
  isSearchExpanded,
  setIsSearchExpanded
}: Props): JSX.Element {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const onDismiss = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleSortTypeChange = useCallback(
    option => {
      setSortingLabel(option.label);
      setSortingType(option.value);
      setIsOpen(false);
      setIsSearchExpanded(false);

      router.push(prepareUrl(pathname, searchParams, { key: SORT_QUERY_PARAM, value: option.value }));
      window.scrollTo(0, 0);
    },
    [setSortingLabel, setSortingType, setIsSearchExpanded, router, pathname, searchParams]
  );
  const triggerRef = useRef<HTMLDivElement | null>(null);

  return (
    <>
      <div className="flex items-center gap-4">
        <div className="mx-2 flex gap-2" ref={triggerRef} data-cvent-id="sort-by">
          <TextLink
            ariaLabel={sortingLabel}
            disabled={false}
            className={`flex font-normal text-brand-60 ${isSearchExpanded ? 'text-b-lg/tight' : 'text-b-md/tight'}`}
            onClick={e => {
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
            onKeyUp={e => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                setIsOpen(!isOpen);
              }
            }}
          >
            <div className={`mr-2 ${isSearchExpanded ? '' : 'mt-0.5'}`}>{sortingLabel}</div>
            <Icon id="chevron-down" className="mt-1 flex h-4 w-4 gap-2 fill-brand-60" />
          </TextLink>
        </div>
      </div>
      <div>
        {isOpen && (
          <Popper
            className="w-full rounded-lg"
            isOpen={isOpen}
            hasShadow
            onDismiss={onDismiss}
            triggerRef={triggerRef}
            placement="bottom-end"
            preventOverflow
          >
            <div className="w-56 rounded-xl bg-background">
              <div className="px-3 py-3">
                <div className="text-bold py-2 text-b-md/tight font-bold text-neutral-60">{sortByLabel}</div>
                {sortOptions.map(option => (
                  <div
                    key={option.value}
                    defaultValue={sortingLabel}
                    className={`${
                      sortingLabel === option.label ? 'rounded-lg bg-brand-20 py-2' : ''
                    } bg-background py-2 text-b-md/tight hover:rounded-lg hover:bg-neutral-20`}
                    role="button"
                    tabIndex={0}
                    onKeyUp={e => {
                      if (e.key === 'Enter') {
                        handleSortTypeChange(option);
                      }
                    }}
                    onClick={() => handleSortTypeChange(option)}
                    data-cvent-id={`sort-by-${convertToSnakeCase(option.label)}`}
                  >
                    <div className="pb-1 pl-2">{option.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </Popper>
        )}
      </div>
    </>
  );
}
